import { BuildingOfficeIcon, HashtagIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';

import type { Carrier } from '@/components/carrier-directory/types';
import { carriersApiService } from '@/services/api/carriersApiService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Carrier entity type
 */
export type CarrierEntity = Carrier;

/**
 * Carrier tooltip content component
 * Pure presentation component - memoized for performance
 */
export const CarrierTooltipContent = memo<
  EntityTooltipContentProps<CarrierEntity>
>(({ fetchState }) => {
  // Handle loading state
  if (fetchState.status === 'pending') {
    return <LoadingState rows={1} />;
  }

  // Handle error state
  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="carrier" />;
  }

  // Handle idle or empty state - no data yet
  if (fetchState.status === 'idle') {
    return <EmptyState entityType="carrier" />;
  }

  // Success state
  const carrier = fetchState.data;
  const { name, normalizedName, aliases } = carrier;

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader icon={BuildingOfficeIcon} title={name || normalizedName} />

      <TooltipSection withBorder>
        {normalizedName && normalizedName !== name && (
          <InfoRow
            icon={BuildingOfficeIcon}
            label="Normalized Name"
            value={normalizedName}
          />
        )}
        {aliases && aliases.length > 0 && (
          <InfoRow
            icon={HashtagIcon}
            label="Aliases"
            value={aliases.join(', ')}
          />
        )}
      </TooltipSection>
    </div>
  );
});

CarrierTooltipContent.displayName = 'CarrierTooltipContent';

/**
 * Hoverable carrier link with tooltip preview
 *
 * Shows carrier name and aliases on hover.
 * Click to navigate to carrier detail page (if available, otherwise just tooltip).
 *
 * @example
 * ```tsx
 * <CarrierLink entityId={carrierId}>
 *   Geico
 * </CarrierLink>
 * ```
 */
export const CarrierLink = createEntityLink<CarrierEntity, string>({
  tooltipContent: CarrierTooltipContent,
  // Assuming there is a carrier detail page, otherwise remove getDetailUrl
  getDetailUrl: (id) => `/carriers/${id}`,
  useQuery: carriersApiService.endpoints.getCarrier.useQuery,
  getQueryArg: (id) => id,
  displayName: 'CarrierLink',
});
